import PageSettings from '../popups/PageSettings.svelte';
import CircleStyling from '../popups/CircleStyling.svelte';
import { makeLmsRequest } from './LmsClient';
import { External, M } from '../../external';
import { ignore } from '../../Common/tools';
import { LmsUtils } from '../utils/LmsUtils';
import { LmsCircleUtils } from '../circles/LmsCircleUtils';
import { buildLocalizedPayload, PAGE_LOCALIZED_FIELDS } from '../utils/LmsLocaleUtils';

export function setCircleSettings(circle) {
  const defaults = {
    showCircleAvatar: true,
    showCircleMember: false,
    showCircleMemberCountInNavigation: false,
    showDescription: false,
    showTrackingProgress: false,
    autoresponders: {},
    tags: [],
    enableDoubleOptIn: false,
    requestAccess: {
      enabled: false,
      title: M.QUESTIONNARIE_TITLE_EXAMPLE,
      description: M.QUESTIONNARIE_DESC_EXAMPLE,
      questions: []
    }
  };
  circle.settings = {
    ...defaults,
    ...(circle.settings || {})
  };

  return circle.settings;
}

export function setGroupSettings(pageGroup) {
  const defaults = {
    showTrackingProgress: false,
    showDescription: false,
    showBadge: false,
    layout: 'text',
    layoutIcon: '',
    layoutImage: '',
    layoutImageBase: '',
    badgeLabel: '',
    hidden: true
  };
  pageGroup.settings = {
    ...defaults,
    ...(pageGroup.settings || {})
  };
  return pageGroup.settings;
}

export function setPageSettings(page) {
  const defaults = {
    trackContentCompletion: false,
    showDescription: false,
    showBadge: false,
    layout: 'text',
    layoutIcon: '',
    layoutImage: '',
    layoutImageBase: '',
    badgeLabel: '',
    hidden: true
  };
  page.settings = {
    ...defaults,
    ...(page.settings || {})
  };
  return page.settings;
}

export function setIconStyling(styling) {
  const defaults = {
    fg: {
      color: '#444',
      colorClass: ''
    },
    bg: {
      color: '#f2f2f8',
      colorClass: ''
    },
    hasBorder: false,
    border: {
      color: '#f2f2f8',
      colorClass: ''
    }
  };
  return {
    ...defaults,
    ...(styling || {})
  };
}

export function setCircleStyling(circle) {
  const defaults = {
    bg: {
      color: '#fff',
      colorClass: ''
    },
    navBg: {
      color: '#fff',
      colorClass: ''
    },
    darkTheme: false,
    circleStyling: false,
    addShadow: false,
    fixToViewportHeight: false,
    viewportHeight: 96,
    navigationWidth: 330,
    textStyling: {
      circleNameFont: 'Arial',
      circleNameTextSize: 21,
      nameFg: {
        color: '#444',
        colorClass: ''
      },
      pageNameFont: 'Arial',
      pageNameTextSize: 14,
      fg: {
        color: '#444',
        colorClass: ''
      },
      badgeFg: {
        color: '#444',
        colorClass: ''
      },
      badgeBg: {
        color: '#f2f2f8',
        colorClass: ''
      }
    },
    shadow: {
      x: 5,
      y: 10,
      blur: 25,
      spread: 15,
      color: 'rgba(170, 170, 170, 0.25)',
      colorClass: ''
    },
    menuSize: 'wide',
    submenuStyle: 'sidebar',
    hasExpandButton: true,
    hasProfileButton: true,
    showCirclePadding: false,
    circlePadding: 0,
    iconStyling: setIconStyling({}),
    thumbnail: {
      type: 'image',
      size: 'small',
      image: {
        name: '',
        base: ''
      },
      icon: {
        value: 'home',
        bg: {
          color: '#f2f2f8',
          colorClass: ''
        },
        fg: {
          color: '#444',
          colorClass: ''
        }
      },
      coverBg: {
        color: '',
        colorClass: ''
      }
    },
    sidebar: {
      position: 'right',
      bg: {
        color: '#fff',
        colorClass: ''
      },
      textStyling: {
        groupNameFont: 'Arial',
        groupNameTextSize: 14,
        fg: {
          color: '#444',
          colorClass: ''
        },
        badgeFg: {
          color: '#444',
          colorClass: ''
        },
        badgeBg: {
          color: '#f2f2f8',
          colorClass: ''
        }
      },
      iconStyling: setIconStyling({})
    }
  };
  circle.styling = {
    ...defaults,
    ...(circle.styling || {})
  };
  return circle.styling;
}

export let showPageSettings = (circleDetails, page, isTemplate) => {
  return new Promise((resolve) => {
    let changed = false;
    External.Tools.showWindow({
      isModal: false,
      withDarkBackground: true,
      width: 420,
      buttons: [],
      noNav: true,
      noTitle: true,
      forceAtRight: window.showBuilderPopupsOnFullHeight,
      action: {
        onShow: (popup, ui) => {
          popup.bindComponent(
            new PageSettings({
              target: popup.getContentArea(),
              props: {
                circleDetails: circleDetails,
                page: page,
                isTemplate: isTemplate,
                onChange: () => {
                  changed = true;
                }
              }
            })
          );
        },
        onHide: async () => {
          if (changed) {
            let payload = buildLocalizedPayload(page, PAGE_LOCALIZED_FIELDS);
            let result = await makeLmsRequest('/page/' + page.id, 'PUT', payload);
            let changedPage = await result.json();
            resolve(changedPage);
          } else {
            resolve(null);
          }
        }
      }
    });
  });
};

export let addGroup = (circleId, uiOrder, doAfter) => {
  External.Tools.promptForInput(M.CREATE_NEW_GROUP, M.ENTER_GROUP_NAME, '', async (name) => {
    let result = await makeLmsRequest('/circle/' + circleId + '/group', 'POST', {
      name: name,
      url: External.Tools.urlFromString(name),
      uiOrder: uiOrder
    });
    let createdGroup = await result.json();
    doAfter(createdGroup);
  });
};

export let addPage = (circleId, groupId, uiOrder, onAfter) => {
  External.Tools.promptForInput(M.CREATE_NEW_PAGE, M.ENTER_PAGE_NAME, '', async (name) => {
    let result = await makeLmsRequest('/page', 'POST', {
      circleId: circleId,
      groupId: groupId,
      name: name,
      url: External.Tools.urlFromString(name),
      uiOrder: uiOrder
    });
    onAfter(await result.json());
  });
};

export let showSettingsPopup = (options) => {
  External.Tools.showWindow({
    isModal: false,
    withDarkBackground: true,
    width: options.width || 420,
    buttons: [],
    noNav: true,
    noTitle: true,
    forceAtRight: window.showBuilderPopupsOnFullHeight,
    action: {
      onShow: (popup, ui) => {
        popup.bindComponent(options.component(popup.getContentArea(), popup.close));
      },
      onHide: options.onHide || ignore
    }
  });
};

export let showCircleStyling = (circle, onSave, onChange) => {
  let changed = false;
  showSettingsPopup({
    component: (target) => {
      return new CircleStyling({
        target: target,
        props: {
          circle: circle,
          onChange: () => {
            changed = true;
            if (onChange) {
              onChange();
            }
          }
        }
      });
    },
    onHide: () => {
      if (changed) {
        onSave();
      }
    }
  });
};

export const addNewEmptyCircle = async (name) => {
  let result = await makeLmsRequest('/circle', 'POST', {
    name: name,
    url: External.Tools.urlFromString(name)
  });
  let circle = await result.json();
  LmsUtils.clearCachedCircles();
  External.CPanel.goToUrl('/me/community/circles/' + circle.url);
};

export const addNewCircle = () => {
  External.UITools.showNewLmsCirclePopup(async (template) => {
    External.Tools.promptForInput(M.CREATE_NEW_CIRCLE, M.ENTER_CIRCLE_NAME, '', async (name) => {
      if (template === null) {
        addNewEmptyCircle(name);
      } else {
        let newCircle = await LmsCircleUtils.newCircleFromTemplate(template, name);
        if (newCircle) {
          External.CPanel.goToUrl('/me/community/circles/' + newCircle.url);
        }
      }
    });
  });
};
